import type { Account, ExpenseInput, IOU } from "../../types";
import { money } from "../../lib/finance";
import DateInput from "../../components/DateInput";

// Turning an ordinary entry into a loan, or a repayment of one. Lending and borrowing are the same
// movement of cash seen from either side, so the wording follows whether the entry is money in or
// money out rather than asking twice.

export interface ExpenseLoanFieldsProps {
  input: ExpenseInput;
  account: Account | undefined;
  isIncome: boolean;
  isIOU: boolean;
  iouMode: "" | "new" | "settle";
  setIouMode: (mode: "" | "new" | "settle") => void;
  iouDescription: string;
  setIouDescription: (value: string) => void;
  iouDueDate: string;
  setIouDueDate: (value: string) => void;
  iouChoice: string;
  setIouChoice: (value: string) => void;
  /** This payee's open loans, already narrowed to the ones this entry could settle. */
  openIouMatches: IOU[];
}

export default function ExpenseLoanFields({
  input, account, isIncome, isIOU, iouMode, setIouMode, iouDescription, setIouDescription,
  iouDueDate, setIouDueDate, iouChoice, setIouChoice, openIouMatches,
}: ExpenseLoanFieldsProps) {
  return (
    <section className="household-share">
      <label>
        Loan
        <select
          value={iouMode}
          onChange={(e) => {
            setIouMode(e.target.value as "" | "new" | "settle");
            setIouChoice("");
            setIouDescription("");
            setIouDueDate("");
          }}
        >
          <option value="">Not a loan</option>
          <option value="new">{isIncome ? "Borrowing" : "Lending"}</option>
          <option value="settle">{isIncome ? "Getting paid back" : "Paying back a loan"}</option>
        </select>
      </label>
      {isIOU && !input.payeeId && <small>Choose a payee above first.</small>}
      {iouMode === "new" && input.payeeId && (
        <>
          <label>Description<input required value={iouDescription} onChange={(e) => setIouDescription(e.target.value)} placeholder="e.g. Personal loan, PS5 sale" maxLength={300} /></label>
          <label>Due date<DateInput value={iouDueDate} onChange={(e) => setIouDueDate(e.target.value)} /></label>
        </>
      )}
      {iouMode === "settle" && input.payeeId && (
        openIouMatches.length ? (
          <label>
            Paying off
            <select required value={iouChoice} onChange={(e) => setIouChoice(e.target.value)}>
              <option value="" disabled>Choose an IOU</option>
              {openIouMatches.map((i) => (
                <option key={i.id} value={i.id}>{i.description} · {money(i.outstanding, i.currency)} outstanding</option>
              ))}
            </select>
          </label>
        ) : (
          <small>No open {account?.currency} IOUs with this payee to pay off.</small>
        )
      )}
    </section>
  );
}
